import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
const PILLARS = [{
  step: '01',
  title: 'Synthesized',
  stat: '≥99%',
  statLabel: 'Avg. HPLC purity',
  body: 'Each peptide is produced in licensed UK facilities to pharmaceutical-grade analytical standards. No imported intermediates.'
}, {
  step: '02',
  title: 'Verified',
  stat: '100%',
  statLabel: 'Batches tested',
  body: 'Every batch is sent to an ISO 17025 accredited UK lab for HPLC purity and mass spectrometry identity verification before sale.'
}, {
  step: '03',
  title: 'Documented',
  stat: 'Every vial',
  statLabel: 'Batch-traceable',
  body: 'A signed Certificate of Analysis with full chromatogram is published for every batch. Look up your batch ID, see the data.'
}, {
  step: '04',
  title: 'Dispatched',
  stat: '< 24h',
  statLabel: 'Before 2pm UK',
  body: 'Same-day cold-chain dispatch from our UK facility in plain, temperature-controlled, discreet packaging.'
}];
const AUDITORS = ['Eurofins UK', 'ISO 17025 accredited', 'UKAS audited', 'MHRA-licensed facility'];
export function WhyUs() {
  return <section className="w-full py-20 lg:py-28 bg-secondary/40 border-y border-border" data-id="element-790">
      <div className="container mx-auto" data-id="element-791">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mb-14 lg:mb-20" data-id="element-792">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-4" data-id="element-793">
            The peptides<span className="text-primary" data-id="element-794">.ml</span> standard
          </p>
          <h2 className="text-3xl lg:text-[48px] font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-795">
            Every vial, the same lab.
            <br data-id="element-796" />
            Every claim, on the certificate
            <span className="text-primary" data-id="element-797">.</span>
          </h2>
          <p className="mt-6 text-[16px] leading-relaxed text-muted-foreground max-w-2xl" data-id="element-798">
            Research-grade peptides require research-grade process. We
            synthesize, independently verify, document, and ship every batch
            under the same protocol — published on the COA, traceable by batch
            ID, and validated by accredited UK third parties.
          </p>
        </motion.div>

        {/* 4-step process pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border" data-id="element-799">
          {PILLARS.map(({
          step,
          title,
          stat,
          statLabel,
          body
        }, i) => <motion.div key={title} initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.45,
          delay: i * 0.06
        }} className="flex flex-col bg-card p-7 lg:p-8 relative" data-id="element-800">
              {/* Step indicator */}
              <div className="flex items-center justify-between mb-6" data-id="element-801">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium" data-id="element-802">
                  Step {step}
                </span>
                <span className="h-1 w-1 rounded-full bg-primary" data-id="element-803" />
              </div>

              {/* Big stat */}
              <div className="font-mono text-[32px] lg:text-[36px] font-medium text-foreground tracking-[-0.02em] leading-none [font-feature-settings:'tnum']" data-id="element-804">
                {stat}
              </div>
              <div className="mt-2 text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-805">
                {statLabel}
              </div>

              {/* Divider */}
              <div className="h-px w-10 bg-border my-5" data-id="element-806" />

              {/* Title + body */}
              <h3 className="text-[17px] font-semibold tracking-tight text-foreground leading-snug mb-2" data-id="element-807">
                {title}
              </h3>
              <p className="text-[13px] leading-relaxed text-muted-foreground" data-id="element-808">
                {body}
              </p>
            </motion.div>)}
        </div>

        {/* Bottom: social proof + auditors */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.45,
        delay: 0.2
      }} className="mt-10 lg:mt-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 pt-8 border-t border-border" data-id="element-809">
          {/* Trustpilot */}
          <div className="flex items-center gap-4" data-id="element-810">
            <div className="flex items-center gap-0.5" data-id="element-811">
              {Array.from({
              length: 5
            }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-primary text-primary" strokeWidth={0} data-id="element-812" />)}
            </div>
            <div className="flex items-center gap-2 text-[13px]" data-id="element-813">
              <span className="font-mono font-medium text-foreground" data-id="element-814">4.8</span>
              <span className="text-muted-foreground" data-id="element-815">on Trustpilot</span>
              <span className="text-border" data-id="element-816">·</span>
              <span className="font-mono text-muted-foreground" data-id="element-817">
                1,247 reviews
              </span>
            </div>
          </div>

          {/* Auditors */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2" data-id="element-818">
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium" data-id="element-819">
              Audited by
            </span>
            {AUDITORS.map(a => <span key={a} className="text-[12.5px] font-semibold tracking-tight text-foreground/70" data-id="element-820">
                {a}
              </span>)}
          </div>
        </motion.div>
      </div>
    </section>;
}